import React, { useRef, useEffect, useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import './SignatureCanvas.css';

export interface SignatureCanvasRef {
  clear: () => void;
  isEmpty: () => boolean;
  toDataURL: (type?: string, quality?: number) => string;
}

interface SignatureCanvasProps {
  width?: number;
  height?: number;
  lineWidth?: number;
  lineColor?: string;
  backgroundColor?: string;
  className?: string;
}

const SignatureCanvas = forwardRef<SignatureCanvasRef, SignatureCanvasProps>(({
  width = 500,
  height = 200,
  lineWidth = 2,
  lineColor = '#000000',
  backgroundColor = '#ffffff',
  className = ''
}, ref) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  const lastPosition = useRef<{ x: number; y: number } | null>(null);

  const getContext = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    return canvas.getContext('2d');
  }, []);

  const initCanvas = useCallback(() => {
    const ctx = getContext();
    if (!ctx) return;

    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, width, height);
    ctx.strokeStyle = lineColor;
    ctx.lineWidth = lineWidth;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, [getContext, backgroundColor, width, height, lineColor, lineWidth]);

  useEffect(() => {
    initCanvas();
  }, [initCanvas]);

  const getPointerPosition = useCallback((e: React.MouseEvent | React.TouchEvent): { x: number; y: number } => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    if ('touches' in e) {
      const touch = e.touches[0];
      return {
        x: (touch.clientX - rect.left) * scaleX,
        y: (touch.clientY - rect.top) * scaleY
      };
    }

    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  }, []);

  const startDrawing = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    const pos = getPointerPosition(e);
    lastPosition.current = pos;
    setIsDrawing(true);
  }, [getPointerPosition]);

  const draw = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawing) return;
    e.preventDefault();

    const ctx = getContext();
    if (!ctx || !lastPosition.current) return;

    const pos = getPointerPosition(e);

    ctx.beginPath();
    ctx.moveTo(lastPosition.current.x, lastPosition.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();

    lastPosition.current = pos;
    setHasDrawn(true);
  }, [isDrawing, getContext, getPointerPosition]);

  const stopDrawing = useCallback(() => {
    setIsDrawing(false);
    lastPosition.current = null;
  }, []);

  const clear = useCallback(() => {
    const ctx = getContext();
    if (!ctx) return;

    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, width, height);
    ctx.strokeStyle = lineColor;
    ctx.lineWidth = lineWidth;
    setHasDrawn(false);
  }, [getContext, backgroundColor, width, height, lineColor, lineWidth]);

  const isEmpty = useCallback(() => {
    return !hasDrawn;
  }, [hasDrawn]);

  const toDataURL = useCallback((type: string = 'image/png', quality: number = 1.0) => {
    const canvas = canvasRef.current;
    if (!canvas) return '';
    return canvas.toDataURL(type, quality);
  }, []);

  useImperativeHandle(ref, () => ({
    clear,
    isEmpty,
    toDataURL
  }), [clear, isEmpty, toDataURL]);

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      className={`signature-canvas ${className}`}
      data-testid="signature-canvas"
      onMouseDown={startDrawing}
      onMouseMove={draw}
      onMouseUp={stopDrawing}
      onMouseLeave={stopDrawing}
      onTouchStart={startDrawing}
      onTouchMove={draw}
      onTouchEnd={stopDrawing}
      onTouchCancel={stopDrawing}
    />
  );
});

SignatureCanvas.displayName = 'SignatureCanvas';

export default SignatureCanvas;
